import { useEffect, useState } from 'react';
import { useNetworkStore, useGameStore } from '@/stores';
import { NETWORK_CONFIG } from '@/utils/constants';
import './NetworkStatusIndicator.css';

/**
 * ネットワーク状態インジケーター
 * オンラインモード時に接続状態、レイテンシ、プレイヤー切断状態を表示
 */
const NetworkStatusIndicator = () => {
  const gameMode = useGameStore((state) => state.mode);
  const connectionStatus = useNetworkStore((state) => state.connectionStatus);
  const reconnectAttempts = useNetworkStore((state) => state.reconnectAttempts);
  const latency = useNetworkStore((state) => state.latency);
  const playersInRoom = useNetworkStore((state) => state.playersInRoom);
  const connectionError = useNetworkStore((state) => state.connectionError);

  // カウントダウン更新用のstate
  const [, setTick] = useState(0);

  // 切断プレイヤーのカウントダウン更新（1秒ごと）
  useEffect(() => {
    const disconnectedPlayers = playersInRoom.filter(
      p => !p.isConnected && !p.isAI && p.disconnectedAt
    );

    if (disconnectedPlayers.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [playersInRoom]);

  // オンラインモード以外では表示しない
  if (gameMode !== 'online') {
    return null;
  }

  // 切断中のプレイヤー（AIではない）
  const disconnectedPlayers = playersInRoom.filter(
    p => !p.isConnected && !p.isAI && p.disconnectedAt
  );

  // AI化されたプレイヤー
  const aiPlayers = playersInRoom.filter(p => p.isAI);

  // 接続状態に応じたアイコンとテキスト
  const getStatusDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          icon: null,
          dotClass: 'NetworkStatusIndicator__dot--connected',
          text: '接続中',
          showLatency: true
        };
      case 'connecting':
        return {
          icon: null,
          dotClass: 'NetworkStatusIndicator__dot--connecting',
          text: '接続中...',
          showLatency: false
        };
      case 'reconnecting':
        return {
          icon: null,
          dotClass: 'NetworkStatusIndicator__dot--reconnecting',
          text: `再接続中... (${reconnectAttempts}/${NETWORK_CONFIG.RECONNECT_ATTEMPTS})`,
          showLatency: false
        };
      case 'disconnected':
        return {
          icon: null,
          dotClass: 'NetworkStatusIndicator__dot--disconnected',
          text: connectionError || '切断されました',
          showLatency: false
        };
      default:
        return {
          icon: null,
          dotClass: '',
          text: '',
          showLatency: false
        };
    }
  };

  // レイテンシの色を決定
  const getLatencyClass = () => {
    if (latency < 100) return 'NetworkStatusIndicator__latency--good';
    if (latency < 200) return 'NetworkStatusIndicator__latency--medium';
    return 'NetworkStatusIndicator__latency--poor';
  };

  // 切断プレイヤーの残り時間を計算
  const getRemainingTime = (disconnectedAt: number): number => {
    const elapsed = Date.now() - disconnectedAt;
    const remaining = Math.max(0, NETWORK_CONFIG.AI_REPLACEMENT_TIMEOUT - elapsed);
    return Math.ceil(remaining / 1000);
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div
      className="NetworkStatusIndicator"
      role="status"
      aria-live="polite"
      aria-label="ネットワーク状態"
    >
      {/* 接続状態 */}
      <div className="NetworkStatusIndicator__connection">
        <span className={`NetworkStatusIndicator__dot ${statusDisplay.dotClass}`} />
        <span className="NetworkStatusIndicator__status-text">
          {statusDisplay.text}
        </span>
        {statusDisplay.showLatency && latency > 0 && (
          <span className={`NetworkStatusIndicator__latency ${getLatencyClass()}`}>
            ({latency}ms)
          </span>
        )}
      </div>

      {/* 切断プレイヤー警告 */}
      {disconnectedPlayers.length > 0 && (
        <div className="NetworkStatusIndicator__warnings">
          {disconnectedPlayers.map(player => {
            const remaining = player.disconnectedAt
              ? getRemainingTime(player.disconnectedAt)
              : 0;
            return (
              <div
                key={player.id}
                className="NetworkStatusIndicator__warning"
                role="alert"
              >
                <span className="NetworkStatusIndicator__warning-icon">!</span>
                <span className="NetworkStatusIndicator__warning-text">
                  {player.name} 切断中 ({remaining}秒)
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* AI化されたプレイヤー */}
      {aiPlayers.length > 0 && (
        <div className="NetworkStatusIndicator__ai-players">
          {aiPlayers.map(player => (
            <div
              key={player.id}
              className="NetworkStatusIndicator__ai-badge"
            >
              <span className="NetworkStatusIndicator__ai-icon">AI</span>
              <span className="NetworkStatusIndicator__ai-name">{player.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NetworkStatusIndicator;
